// Relevé d'un portail pendant une visite supervisée (docs/visite-portail.md) : ce que l'outil du
// collecteur local garde des requêtes que fait le navigateur du titulaire, pour écrire la
// déclaration d'un adaptateur (domaines, écritures) sans rien deviner. Ni corps, ni en-tête, ni
// cookie, ni paramètre, ni nom de fichier : la méthode, l'hôte et le chemin masqué. Et la structure
// des pages, pour écrire ses sélecteurs : balises et attributs de structure, tout texte et toute
// valeur qui pourrait porter une donnée remplacés par X.

/** Un élément de la page tel que le navigateur le lit : le texte n'en sort jamais (X). */
export type NoeudPage = {
  balise: string
  attributs: [string, string][]
  enfants: (NoeudPage | 'X')[]
}

export type ObservationReleve =
  | { genre: 'requete'; methode: string; url: string; navigation: boolean }
  | { genre: 'telechargement'; url: string; nomPropose: string }
  | {
      genre: 'structure'
      url: string
      /** Cadre (iframe) plutôt que la page elle-même. */
      cadre: boolean
      /** Relevée au chargement (ou en cours de visite), ou au moment d'un téléchargement. */
      moment: 'page' | 'telechargement'
      racine: NoeudPage
    }

export type StructureReleve = {
  hote: string
  chemin: string
  cadre: boolean
  moment: 'page' | 'telechargement'
  squelette: string
}

export type SyntheseReleve = {
  /** Hôte de l'adresse de départ. */
  depart: string
  /** Hôtes sollicités, du plus au moins sollicité : candidats aux domaines déclarés. */
  domaines: { hote: string; requetes: number; navigations: number }[]
  /** Requêtes autres que GET : candidates aux écritures déclarées (connexion, code, liste). */
  ecritures: { methode: string; hote: string; chemin: string; nombre: number }[]
  /** Pages principales visitées, dans l'ordre (le parcours de la connexion à la liste). */
  navigations: { hote: string; chemin: string }[]
  telechargements: { hote: string; chemin: string; extension: string | null }[]
  /** Squelettes distincts des pages et des cadres, de quoi écrire les sélecteurs. */
  structures: StructureReleve[]
}

const LIGNES_MAX = 300
const STRUCTURES_MAX = 60
const METHODES_LECTURE = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Morceau sans donnée : chiffres en `#` ; long, d'allure de jeton (lettres et chiffres mêlés,
 * douze caractères ou plus) ou portant une adresse : `*`.
 */
function morceauMasque(s: string): string {
  if (s === '') return ''
  if (s.length > 40 || /@|%40/i.test(s)) return '*'
  if (s.length >= 12 && /\d/.test(s) && /[a-z]/i.test(s)) return '*'
  return s.replace(/\d+/g, '#')
}

/** Chemin sans donnée, morceau par morceau (`morceauMasque`). Ni requête ni fragment. */
export function cheminMasque(chemin: string): string {
  const sansSuite = chemin.split(/[?#]/)[0] ?? ''
  return sansSuite.split('/').map(morceauMasque).join('/')
}

/**
 * Attributs dont la valeur décrit la structure (sélecteurs) : gardés, masqués morceau par
 * morceau. Le navigateur ne transmet en clair que ceux-ci et les adresses ; tout autre attribut
 * arrive déjà en X.
 */
export const ATTRIBUTS_STRUCTURE: readonly string[] = [
  'id',
  'class',
  'role',
  'type',
  'name',
  'for',
  'method',
  'autocomplete',
  'inputmode',
  'target',
  'rel',
  'tabindex',
  'maxlength',
  'minlength',
  'formcontrolname',
  'data-testid',
  'data-test',
  'data-qa',
  'data-cy',
  'aria-hidden',
  'aria-expanded',
  'aria-selected',
  'aria-checked',
  'aria-disabled',
  'aria-required',
  'aria-haspopup',
  'aria-controls',
  'aria-labelledby',
  'aria-describedby',
  'aria-current',
  'aria-live',
  'disabled',
  'required',
  'readonly',
  'checked',
  'hidden',
  'multiple',
]
/** Attributs d'adresse : réduits à l'hôte (s'il est autre) et au chemin masqué. */
export const ATTRIBUTS_ADRESSE: readonly string[] = [
  'href',
  'src',
  'action',
  'formaction',
  'routerlink',
]
/** Attributs de texte (ou d'une donnée possible) : nom gardé, valeur en X. */
const ATTRIBUTS_TEXTE = new Set([
  'title',
  'alt',
  'placeholder',
  'value',
  'label',
  'content',
  'download',
])
const STRUCTURE = new Set(ATTRIBUTS_STRUCTURE)
const ADRESSE = new Set(ATTRIBUTS_ADRESSE)
const VIDES = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
])
const PROFONDEUR_MAX = 60
const LIGNES_SQUELETTE_MAX = 3000
const REPETITIONS_GARDEES = 3

function adresseMasquee(v: string): string {
  const a = v.trim()
  if (a === '') return ''
  if (a.startsWith('#')) return '#'
  if (/^javascript:/i.test(a)) return 'javascript:'
  try {
    const u = new URL(a, 'https://relatif.invalid')
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'X'
    const chemin = cheminMasque(u.pathname)
    return u.hostname === 'relatif.invalid' ? chemin : `${u.protocol}//${u.hostname}${chemin}`
  } catch {
    return 'X'
  }
}

function attributMasque(nom: string, valeur: string): string | null {
  const n = nom.toLowerCase()
  if (!/^[a-z][a-z0-9_:.-]*$/.test(n) || n.startsWith('on') || n === 'style') return null
  if (ADRESSE.has(n)) return adresseMasquee(valeur)
  if (STRUCTURE.has(n)) {
    if (valeur.length > 120 || /@|%40/i.test(valeur)) return '*'
    // Classe ou identifiant composé (« contrat-004512 ») : chaque partie à part.
    return valeur
      .trim()
      .split(/\s+/)
      .map((mot) =>
        mot
          .split(/([-_:.])/)
          .map(morceauMasque)
          .join(''),
      )
      .join(' ')
  }
  if (ATTRIBUTS_TEXTE.has(n) || n.startsWith('data-') || n.startsWith('aria-'))
    return valeur.trim() === '' ? '' : 'X'
  return null
}

function baliseMasquee(b: string): string {
  const n = b.toLowerCase()
  return n === '#shadow-root' || /^[a-z][a-z0-9-]*$/.test(n) ? n : 'X'
}

function lignesNoeud(n: NoeudPage, profondeur: number): string[] {
  const balise = baliseMasquee(n.balise)
  let attributs = ''
  for (const [nom, valeur] of n.attributs) {
    const v = attributMasque(nom, valeur)
    if (v === null) continue
    attributs +=
      v === '' ? ` ${nom.toLowerCase()}` : ` ${nom.toLowerCase()}="${v.replace(/"/g, '&quot;')}"`
  }
  const ouvre = `<${balise}${attributs}>`
  if (VIDES.has(balise)) return [ouvre]
  // Textes voisins : un seul X.
  const enfants = n.enfants.filter((e, i) => !(e === 'X' && n.enfants[i - 1] === 'X'))
  if (enfants.length === 0) return [`${ouvre}</${balise}>`]
  if (enfants.length === 1 && enfants[0] === 'X') return [`${ouvre}X</${balise}>`]
  if (profondeur >= PROFONDEUR_MAX) return [`${ouvre}…</${balise}>`]
  const corps: string[] = []
  let precedent: string | null = null
  let identiques = 0
  const conclure = () => {
    if (identiques > REPETITIONS_GARDEES)
      corps.push(`  <!-- et ${identiques - REPETITIONS_GARDEES} identiques -->`)
  }
  for (const e of enfants) {
    const bloc = e === 'X' ? ['X'] : lignesNoeud(e, profondeur + 1)
    const cle = bloc.join('\n')
    if (cle === precedent) identiques++
    else {
      conclure()
      precedent = cle
      identiques = 1
    }
    if (identiques <= REPETITIONS_GARDEES) for (const l of bloc) corps.push(`  ${l}`)
  }
  conclure()
  return [ouvre, ...corps, `</${balise}>`]
}

/**
 * Squelette lisible d'une page : balises imbriquées, attributs de structure masqués, texte et
 * autres valeurs en X, adresses réduites, lignes identiques comptées. Borné en profondeur et en
 * longueur.
 */
export function squeletteMasque(racine: NoeudPage): string {
  const lignes = lignesNoeud(racine, 0)
  if (lignes.length > LIGNES_SQUELETTE_MAX)
    return [...lignes.slice(0, LIGNES_SQUELETTE_MAX), '<!-- tronqué -->'].join('\n')
  return lignes.join('\n')
}

function lire(url: string): { hote: string; chemin: string } | null {
  try {
    let u = new URL(url)
    // Un blob ou un fichier créé par la page : l'origine compte, le reste est un identifiant.
    if (u.protocol === 'blob:') return { hote: new URL(u.pathname).hostname, chemin: '/*' }
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    u = new URL(u.origin + u.pathname)
    return { hote: u.hostname.toLowerCase(), chemin: cheminMasque(u.pathname) }
  } catch {
    return null
  }
}

export function syntheseReleve(
  depart: string,
  observations: readonly ObservationReleve[],
): SyntheseReleve {
  const domaines = new Map<string, { hote: string; requetes: number; navigations: number }>()
  const ecritures = new Map<string, SyntheseReleve['ecritures'][number]>()
  const navigations: SyntheseReleve['navigations'] = []
  const telechargements = new Map<string, SyntheseReleve['telechargements'][number]>()
  const structures = new Map<string, StructureReleve>()
  for (const o of observations) {
    const l = lire(o.url)
    if (!l) continue
    if (o.genre === 'structure') {
      const squelette = squeletteMasque(o.racine)
      const cle = JSON.stringify([l.hote, l.chemin, o.cadre, squelette])
      const deja = structures.get(cle)
      // La même page vue au moment d'un téléchargement : c'est la liste des documents.
      if (deja) {
        if (o.moment === 'telechargement') deja.moment = 'telechargement'
      } else if (structures.size < STRUCTURES_MAX)
        structures.set(cle, { ...l, cadre: o.cadre, moment: o.moment, squelette })
      continue
    }
    if (o.genre === 'telechargement') {
      const extension = /\.([a-z0-9]{2,5})$/i.exec(o.nomPropose)?.[1]?.toLowerCase() ?? null
      const t = { ...l, extension }
      telechargements.set(JSON.stringify(t), t)
      continue
    }
    const d = domaines.get(l.hote) ?? { hote: l.hote, requetes: 0, navigations: 0 }
    d.requetes++
    if (o.navigation) d.navigations++
    domaines.set(l.hote, d)
    const methode = o.methode.toUpperCase()
    if (!METHODES_LECTURE.has(methode)) {
      const cle = `${methode} ${l.hote}${l.chemin}`
      const e = ecritures.get(cle) ?? { methode, ...l, nombre: 0 }
      e.nombre++
      ecritures.set(cle, e)
    }
    if (o.navigation) {
      const precedente = navigations.at(-1)
      if (!precedente || precedente.hote !== l.hote || precedente.chemin !== l.chemin)
        navigations.push(l)
    }
  }
  return {
    depart: lire(depart)?.hote ?? '',
    domaines: [...domaines.values()]
      .sort((a, b) => b.requetes - a.requetes || a.hote.localeCompare(b.hote))
      .slice(0, LIGNES_MAX),
    ecritures: [...ecritures.values()]
      .sort(
        (a, b) =>
          b.nombre - a.nombre || `${a.hote}${a.chemin}`.localeCompare(`${b.hote}${b.chemin}`),
      )
      .slice(0, LIGNES_MAX),
    navigations: navigations.slice(0, LIGNES_MAX),
    telechargements: [...telechargements.values()].slice(0, LIGNES_MAX),
    structures: [...structures.values()],
  }
}
